import { CSSProperties } from "react";
import { UnitIcon } from "./UnitIcon";

interface Props {
  unitId?: string;
  cls: number | undefined;
  // 0=Fire, 1=Water, 2=Air, 3=Earth. Drives ground-glow + particle color.
  // When undefined, falls back to a neutral whitish glow so the wrapper still
  // works for camp / monster units that lack an element.
  element?: number;
  size?: number;
  rounded?: "md" | "lg" | "xl" | "2xl" | "none";
  // Number of particles emitted inside the card. Default 6. Smaller cards can
  // drop to 3-4 to avoid visual noise.
  particles?: number;
}

// Solid color used to tint the ground halo + the rising particle dots. The
// elementTheme.ts palette uses these same hues for the glow var. We duplicate
// them here so the wrapper can color particles independent of the player's
// element (the global --element-glow follows the player, not the unit).
const GLOW_RGB: Record<number, string> = {
  0: "255, 120, 60",
  1: "70, 170, 255",
  2: "180, 140, 255",
  3: "120, 200, 110",
};

const PARTICLE_COLOR: Record<number, string> = {
  0: "#ffb070",
  1: "#7fd4ff",
  2: "#c8a8ff",
  3: "#9fd982",
};

export function AnimatedUnitIcon({
  unitId,
  cls,
  element,
  size = 64,
  rounded = "xl",
  particles = 6,
}: Props) {
  const rgb = element != null ? GLOW_RGB[element] : "255, 255, 255";
  const particleColor =
    element != null ? PARTICLE_COLOR[element] : "rgba(255,255,255,0.7)";

  const groundGlow = `radial-gradient(ellipse 70% 35% at 50% 92%, rgba(${rgb}, 0.55) 0%, rgba(${rgb}, 0.0) 70%)`;

  return (
    <div className="relative h-full w-full overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: groundGlow }}
      />

      <div aria-hidden className="pointer-events-none absolute inset-0">
        {Array.from({ length: particles }).map((_, i) => {
          const xPct = 12 + ((i * 76) / Math.max(1, particles - 1));
          const style: CSSProperties = {
            left: `${xPct}%`,
            background: particleColor,
            animationDelay: `${(i * 0.45).toFixed(2)}s`,
            animationDuration: `${2.4 + (i % 3) * 0.4}s`,
          };
          return <span key={i} className="animate-unit-particle" style={style} />;
        })}
      </div>

      <div className="animate-unit-breath relative h-full w-full">
        <UnitIcon unitId={unitId} cls={cls} size={size} rounded={rounded} />
      </div>
    </div>
  );
}
